import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api, formatApiError } from "@/lib/api";
import { Home, Heart, FileText, ShieldCheck, Users, Scale, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Phase 5.6 — all 5 lifecycle matter types covered by the Legal Intake & Structuring Agent.
// Phase 9 — Maintenance & Divorce Support (dedicated calculator-first flow).
const SERVICES = [
  {
    key: "conveyancing",
    icon: <Home className="h-6 w-6" />,
    title: "Buying or selling property",
    sub: "Transfers, bonds & OTPs",
    desc: "Property transfer, bond registration, FICA, SARS duty and lodgement.",
    time: "Takes 12–15 minutes to get started",
  },
  {
    key: "antenuptial",
    icon: <Heart className="h-6 w-6" />,
    title: "Getting married",
    sub: "Antenuptial contract (ANC)",
    desc: "ANC — with or without accrual. Notarised before the wedding.",
    time: "Takes 8–10 minutes to get started",
  },
  {
    key: "will",
    icon: <FileText className="h-6 w-6" />,
    title: "Planning your estate",
    sub: "Wills & succession",
    desc: "Will drafting, executor nomination, guardian for minors, bequest structuring.",
    time: "Takes 10–12 minutes to get started",
  },
  {
    key: "trust",
    icon: <ShieldCheck className="h-6 w-6" />,
    title: "Setting up a trust",
    sub: "Inter-vivos or testamentary",
    desc: "Trust formation, trustee selection, Master registration support.",
    time: "Takes 10–12 minutes to get started",
  },
  {
    key: "deceased_estate",
    icon: <Users className="h-6 w-6" />,
    title: "Managing a deceased estate",
    sub: "Estate administration",
    desc: "Reporting to Master, L&D account, creditors, distribution and discharge. Start with the calculator to see executor's fees, estate duty and the all-important liquidity check.",
    time: "Takes 15–20 minutes to get started",
    href: "/calculators/deceased-estate",
  },
  {
    key: "maintenance_divorce",
    icon: <Scale className="h-6 w-6" />,
    title: "Maintenance or divorce",
    sub: "J941 + financial disclosure",
    desc: "Calculate a fair, proportional maintenance contribution and prepare the J941 application. If both parties agree, we make the process faster and simpler.",
    time: "Takes 10–15 minutes to get started",
    href: "/calculators/maintenance",
  },
];

export default function IntakeStart() {
  const [loading, setLoading] = useState(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // OTP-2: deeplink — /intake?type=conveyancing&action=otp routes directly
  // into the dedicated OTP self-service wizard (calculator → OTP CTA path).
  useEffect(() => {
    if (params.get("action") === "otp") {
      const price = params.get("price") || "";
      const pt    = params.get("property_type") || "freehold";
      navigate(`/intake/otp?price=${price}&property_type=${pt}`, { replace: true });
    }
  }, [params, navigate]);

  const start = async (service) => {
    // If the service has a custom href (e.g. maintenance calculator),
    // route there directly — its flow is calculator-first, not chat-first.
    if (service.href) {
      navigate(service.href);
      return;
    }
    const key = service.key;
    setLoading(key);
    try {
      const { data } = await api.post("/intake/start", { service_type: key });
      sessionStorage.setItem(`intake-${data.session_id}`, JSON.stringify({
        service:  key,
        opening:  data.opening,
        step:     data.step,
        progress: data.progress,
      }));
      navigate(`/intake/${data.session_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <SiteHeader />
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-6xl px-6 pt-16 pb-12">
          <div className="flex items-center gap-2 text-overline text-zinc-500">
            <Sparkles className="h-3 w-3" /> Guided intake
          </div>
          <h1 className="mt-4 font-serif text-5xl lg:text-6xl font-medium tracking-tighter leading-none">
            Let&apos;s get you clear on what&apos;s required
          </h1>
          <p className="mt-6 text-zinc-600 leading-relaxed max-w-2xl">
            Answer a few guided questions and we&apos;ll:
          </p>
          <ul className="mt-3 text-zinc-600 leading-relaxed max-w-2xl space-y-1 list-disc pl-5">
            <li>show you exactly what&apos;s required</li>
            <li>flag issues early</li>
            <li>guide you step-by-step</li>
          </ul>
          <p className="mt-4 text-zinc-600 leading-relaxed max-w-2xl">
            Everything is reviewed by an admitted attorney.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
          {SERVICES.map((s) => (
            <button
              key={s.key}
              onClick={() => start(s)}
              disabled={loading === s.key}
              className="text-left p-7 border border-zinc-200 bg-white hover:border-zinc-900 hover:-translate-y-1 transition-all duration-200 group disabled:opacity-50"
              data-testid={`intake-card-${s.key}`}
            >
              <div className="h-12 w-12 bg-zinc-900 text-white grid place-items-center group-hover:bg-[#5a6b86] transition-colors">
                {s.icon}
              </div>
              <h3 className="mt-7 font-serif text-2xl font-medium tracking-tight">{s.title}</h3>
              <div className="text-[10px] tracking-widest uppercase text-zinc-500 mt-1">{s.sub}</div>
              <p className="mt-4 text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
              <div className="mt-7 flex items-center justify-between text-xs">
                <span className="text-overline text-zinc-400">{s.time}</span>
                <span className="inline-flex items-center gap-1 text-zinc-900 font-medium">
                  {loading === s.key ? "Starting…" : "Get started"} <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </button>
          ))}
        </div>
        <p className="mt-12 text-center text-xs text-zinc-500">
          Your information is treated as legally privileged. Every output is reviewed by an admitted attorney before anything legally binding happens.
        </p>
      </section>

      <SiteFooter />
    </div>
  );
}
